import { http } from "@/lib/http";

import type {
  GatewayFlow,
  FlowView,
  GatewayOAuth,
  OAuthConsentRequest,
  GatewayConfig,
  Budget,
  BudgetPutRequest,
  GatewayBudgets,
  Agent,
  AgentBuild,
  AgentBuildLog,
  AgentCreateRequest,
  AgentLogs,
  AgentUpdateRequest,
  CatalogEntry,
  ClientSnippets,
  Credential,
  CredentialCreated,
  CredentialCreateRequest,
  Delegation,
  DelegationCreated,
  DelegationCreateRequest,
  Gateway,
  GatewayCreateRequest,
  GatewayActivity,
  GatewayActivityQuery,
  GatewayLogsQuery,
  Role,
  RoleCreateRequest,
  RoleUpdateRequest,
  ServerTiers,
  Tier,
  GatewayLogsResponse,
  GatewaySecurity,
  GatewaySecurityQuery,
  GatewayTracesQuery,
  OpenApiPreview,
  OpenApiPreviewRequest,
  TelemetryExport,
  TelemetryExportRequest,
  UpstreamReach,
  AdvancedView,
  AdvancedViewSession,
  PlaygroundRunRequest,
  PlaygroundRunResult,
  PlaygroundTargets,
  GatewayTracesResponse,
  AgentEndpoint,
  AgentEndpointCreateRequest,
  AgentEndpointUpdateRequest,
  PublishedAgent,
  PublishRequest,
  GatewayMetrics,
  GatewaySize,
  GatewayUpdateRequest,
  PlaneClasses,
  ModelAliases,
  Provider,
  ProviderCreateRequest,
  ProviderUpdateRequest,
  ToolPolicy,
  ToolServer,
  ToolServerCreateRequest,
  ToolServerDelegation,
  ToolServerUpdateRequest,
  ContentRule,
  ContentRuleCreateRequest,
  ContentRuleSet,
  ContentRuleUpdateRequest,
  GatewayInstall,
} from "./types";

// --- gateways ---

export async function listGateways(): Promise<Gateway[]> {
  const { data } = await http.get<Gateway[]>("/agent-gateways");
  return data;
}

export async function getGateway(gatewayId: string): Promise<Gateway> {
  const { data } = await http.get<Gateway>(`/agent-gateways/${gatewayId}`);
  return data;
}

export async function createGateway(payload: GatewayCreateRequest): Promise<Gateway> {
  const { data } = await http.post<Gateway>("/agent-gateways", payload);
  return data;
}

export async function updateGateway(
  gatewayId: string,
  payload: GatewayUpdateRequest,
): Promise<Gateway> {
  const { data } = await http.put<Gateway>(`/agent-gateways/${gatewayId}`, payload);
  return data;
}

export async function deleteGateway(gatewayId: string): Promise<void> {
  await http.delete(`/agent-gateways/${gatewayId}`);
}

/** The classes the security plane's policies select (AGW-22). */
export async function listPlaneClasses(): Promise<PlaneClasses> {
  const { data } = await http.get<PlaneClasses>("/agent-plane/classes");
  return data;
}

/** Update the gateway to the current software version, in place (AGW-10). */
export async function updateGatewaySoftware(gatewayId: string): Promise<Gateway> {
  const { data } = await http.post<Gateway>(`/agent-gateways/${gatewayId}/update`);
  return data;
}

export async function resizeGateway(gatewayId: string, size: GatewaySize): Promise<Gateway> {
  const { data } = await http.post<Gateway>(`/agent-gateways/${gatewayId}/resize`, { size });
  return data;
}

export async function getClientSnippets(gatewayId: string): Promise<ClientSnippets> {
  const { data } = await http.get<ClientSnippets>(`/agent-gateways/${gatewayId}/client-snippets`);
  return data;
}

/** The certificate authority a client trusts for the encrypted private address (AGW-46). */
export async function fetchGatewayCa(gatewayId: string): Promise<Blob> {
  const { data } = await http.get<Blob>(`/agent-gateways/${gatewayId}/tls/ca`, {
    responseType: "blob",
  });
  return data;
}

// --- tool servers ---

export async function listToolServers(gatewayId: string): Promise<ToolServer[]> {
  const { data } = await http.get<ToolServer[]>(`/agent-gateways/${gatewayId}/tool-servers`);
  return data;
}

export async function getToolServer(gatewayId: string, serverId: string): Promise<ToolServer> {
  const { data } = await http.get<ToolServer>(
    `/agent-gateways/${gatewayId}/tool-servers/${serverId}`,
  );
  return data;
}

export async function createToolServer(
  gatewayId: string,
  payload: ToolServerCreateRequest,
): Promise<ToolServer> {
  const { data } = await http.post<ToolServer>(
    `/agent-gateways/${gatewayId}/tool-servers`,
    payload,
  );
  return data;
}

export async function updateToolServer(
  gatewayId: string,
  serverId: string,
  payload: ToolServerUpdateRequest,
): Promise<ToolServer> {
  const { data } = await http.put<ToolServer>(
    `/agent-gateways/${gatewayId}/tool-servers/${serverId}`,
    payload,
  );
  return data;
}

export async function deleteToolServer(gatewayId: string, serverId: string): Promise<void> {
  await http.delete(`/agent-gateways/${gatewayId}/tool-servers/${serverId}`);
}

/** Runs `tools/list` through the gateway and stores the names on the row. */
export async function discoverTools(gatewayId: string, serverId: string): Promise<ToolServer> {
  const { data } = await http.post<ToolServer>(
    `/agent-gateways/${gatewayId}/tool-servers/${serverId}/discover`,
  );
  return data;
}

// --- model providers ---

export async function listProviders(gatewayId: string): Promise<Provider[]> {
  const { data } = await http.get<Provider[]>(`/agent-gateways/${gatewayId}/providers`);
  return data;
}

export async function getProvider(gatewayId: string, providerId: string): Promise<Provider> {
  const { data } = await http.get<Provider>(`/agent-gateways/${gatewayId}/providers/${providerId}`);
  return data;
}

export async function createProvider(
  gatewayId: string,
  payload: ProviderCreateRequest,
): Promise<Provider> {
  const { data } = await http.post<Provider>(`/agent-gateways/${gatewayId}/providers`, payload);
  return data;
}

export async function updateProvider(
  gatewayId: string,
  providerId: string,
  payload: ProviderUpdateRequest,
): Promise<Provider> {
  const { data } = await http.put<Provider>(
    `/agent-gateways/${gatewayId}/providers/${providerId}`,
    payload,
  );
  return data;
}

export async function deleteProvider(gatewayId: string, providerId: string): Promise<void> {
  await http.delete(`/agent-gateways/${gatewayId}/providers/${providerId}`);
}

// --- agent credentials ---

export async function listCredentials(gatewayId: string): Promise<Credential[]> {
  const { data } = await http.get<Credential[]>(`/agent-gateways/${gatewayId}/credentials`);
  return data;
}

export async function createCredential(
  gatewayId: string,
  payload: CredentialCreateRequest,
): Promise<CredentialCreated> {
  const { data } = await http.post<CredentialCreated>(
    `/agent-gateways/${gatewayId}/credentials`,
    payload,
  );
  return data;
}

export async function revokeCredential(gatewayId: string, credentialId: string): Promise<void> {
  await http.delete(`/agent-gateways/${gatewayId}/credentials/${credentialId}`);
}

// --- roles as saved policies (AGW-44) ---

export async function listRoles(gatewayId: string): Promise<Role[]> {
  const { data } = await http.get<Role[]>(`/agent-gateways/${gatewayId}/roles`);
  return data;
}

export async function createRole(gatewayId: string, payload: RoleCreateRequest): Promise<Role> {
  const { data } = await http.post<Role>(`/agent-gateways/${gatewayId}/roles`, payload);
  return data;
}

export async function updateRole(
  gatewayId: string,
  roleId: string,
  payload: RoleUpdateRequest,
): Promise<Role> {
  const { data } = await http.put<Role>(`/agent-gateways/${gatewayId}/roles/${roleId}`, payload);
  return data;
}

export async function resetRole(gatewayId: string, roleId: string): Promise<Role> {
  const { data } = await http.post<Role>(`/agent-gateways/${gatewayId}/roles/${roleId}/reset`);
  return data;
}

export async function deleteRole(gatewayId: string, roleId: string): Promise<void> {
  await http.delete(`/agent-gateways/${gatewayId}/roles/${roleId}`);
}

/** Move a credential to a role, or off one (`null`: what it may call becomes its own). */
export async function setCredentialRole(
  gatewayId: string,
  credentialId: string,
  roleId: string | null,
): Promise<Credential> {
  const { data } = await http.put<Credential>(
    `/agent-gateways/${gatewayId}/credentials/${credentialId}/role`,
    { role_id: roleId },
  );
  return data;
}

export async function listToolTiers(gatewayId: string): Promise<ServerTiers[]> {
  const { data } = await http.get<ServerTiers[]>(`/agent-gateways/${gatewayId}/tool-tiers`);
  return data;
}

/** Your own tier for a tool (`null` clears it back to what the server says). */
export async function setToolTiers(
  gatewayId: string,
  serverId: string,
  tiers: Record<string, Tier | null>,
): Promise<ServerTiers> {
  const { data } = await http.put<ServerTiers>(
    `/agent-gateways/${gatewayId}/tool-servers/${serverId}/tool-tiers`,
    { tiers },
  );
  return data;
}

// --- delegation (plan AGW-8) ---

export async function listToolServerDelegations(
  gatewayId: string,
): Promise<ToolServerDelegation[]> {
  const { data } = await http.get<ToolServerDelegation[]>(
    `/agent-gateways/${gatewayId}/delegated-tool-servers`,
  );
  return data;
}

export async function setToolServerDelegation(
  gatewayId: string,
  toolServerId: string,
  enabled: boolean,
): Promise<ToolServerDelegation> {
  const { data } = await http.put<ToolServerDelegation>(
    `/agent-gateways/${gatewayId}/tool-servers/${toolServerId}/delegation`,
    { enabled },
  );
  return data;
}

export async function listDelegations(gatewayId: string): Promise<Delegation[]> {
  const { data } = await http.get<Delegation[]>(`/agent-gateways/${gatewayId}/delegations`);
  return data;
}

export async function createDelegation(
  gatewayId: string,
  payload: DelegationCreateRequest,
): Promise<DelegationCreated> {
  const { data } = await http.post<DelegationCreated>(
    `/agent-gateways/${gatewayId}/delegations`,
    payload,
  );
  return data;
}

export async function revokeDelegation(gatewayId: string, delegationId: string): Promise<void> {
  await http.delete(`/agent-gateways/${gatewayId}/delegations/${delegationId}`);
}

export async function getPolicy(gatewayId: string, credentialId: string): Promise<ToolPolicy> {
  const { data } = await http.get<ToolPolicy>(
    `/agent-gateways/${gatewayId}/credentials/${credentialId}/policy`,
  );
  return data;
}

export async function setPolicy(
  gatewayId: string,
  credentialId: string,
  payload: ToolPolicy,
): Promise<ToolPolicy> {
  const { data } = await http.put<ToolPolicy>(
    `/agent-gateways/${gatewayId}/credentials/${credentialId}/policy`,
    payload,
  );
  return data;
}

// --- model aliases (AGW-11) ---

export async function getModelAliases(gatewayId: string): Promise<ModelAliases> {
  const { data } = await http.get<ModelAliases>(`/agent-gateways/${gatewayId}/model-aliases`);
  return data;
}

export async function setModelAliases(
  gatewayId: string,
  payload: ModelAliases,
): Promise<ModelAliases> {
  const { data } = await http.put<ModelAliases>(
    `/agent-gateways/${gatewayId}/model-aliases`,
    payload,
  );
  return data;
}

// --- the catalog (AGW-11): starters proven on this cloud ---

export async function listCatalog(): Promise<CatalogEntry[]> {
  const { data } = await http.get<CatalogEntry[]>("/agent-gateways/catalog");
  return data;
}

// --- agents ---

export async function listAgents(gatewayId: string): Promise<Agent[]> {
  const { data } = await http.get<Agent[]>(`/agent-gateways/${gatewayId}/agents`);
  return data;
}

export async function getAgent(gatewayId: string, agentId: string): Promise<Agent> {
  const { data } = await http.get<Agent>(`/agent-gateways/${gatewayId}/agents/${agentId}`);
  return data;
}

export async function createAgent(gatewayId: string, payload: AgentCreateRequest): Promise<Agent> {
  const { data } = await http.post<Agent>(`/agent-gateways/${gatewayId}/agents`, payload);
  return data;
}

export async function updateAgent(
  gatewayId: string,
  agentId: string,
  payload: AgentUpdateRequest,
): Promise<Agent> {
  const { data } = await http.put<Agent>(`/agent-gateways/${gatewayId}/agents/${agentId}`, payload);
  return data;
}

export async function deleteAgent(gatewayId: string, agentId: string): Promise<void> {
  await http.delete(`/agent-gateways/${gatewayId}/agents/${agentId}`);
}

export async function startAgent(gatewayId: string, agentId: string): Promise<Agent> {
  const { data } = await http.post<Agent>(`/agent-gateways/${gatewayId}/agents/${agentId}/start`);
  return data;
}

export async function stopAgent(gatewayId: string, agentId: string): Promise<Agent> {
  const { data } = await http.post<Agent>(`/agent-gateways/${gatewayId}/agents/${agentId}/stop`);
  return data;
}

/** Pause a running agent where it is (AGW-14); it bills no memory-seconds until resumed. */
export async function pauseAgent(gatewayId: string, agentId: string): Promise<Agent> {
  const { data } = await http.post<Agent>(`/agent-gateways/${gatewayId}/agents/${agentId}/pause`);
  return data;
}

/** Resume a paused agent; it continues where it was (AGW-14). */
export async function resumeAgent(gatewayId: string, agentId: string): Promise<Agent> {
  const { data } = await http.post<Agent>(`/agent-gateways/${gatewayId}/agents/${agentId}/resume`);
  return data;
}

export async function listAgentBuilds(gatewayId: string, agentId: string): Promise<AgentBuild[]> {
  const { data } = await http.get<AgentBuild[]>(
    `/agent-gateways/${gatewayId}/agents/${agentId}/builds`,
  );
  return data;
}

export async function getAgentBuild(
  gatewayId: string,
  agentId: string,
  buildId: string,
): Promise<AgentBuild> {
  const { data } = await http.get<AgentBuild>(
    `/agent-gateways/${gatewayId}/agents/${agentId}/builds/${buildId}`,
  );
  return data;
}

export async function getAgentBuildLog(
  gatewayId: string,
  agentId: string,
  buildId: string,
): Promise<AgentBuildLog> {
  const { data } = await http.get<AgentBuildLog>(
    `/agent-gateways/${gatewayId}/agents/${agentId}/builds/${buildId}/log`,
  );
  return data;
}

export async function getAgentLogs(gatewayId: string, agentId: string): Promise<AgentLogs> {
  const { data } = await http.get<AgentLogs>(`/agent-gateways/${gatewayId}/agents/${agentId}/logs`);
  return data;
}

// --- metrics and logs (AGW-7) ---

export async function getGatewayMetrics(gatewayId: string, hours: number): Promise<GatewayMetrics> {
  const { data } = await http.get<GatewayMetrics>(`/agent-gateways/${gatewayId}/metrics`, {
    params: { hours },
  });
  return data;
}

function compact(
  query: GatewayLogsQuery | GatewayTracesQuery | GatewaySecurityQuery | GatewayActivityQuery,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== "") out[key] = value;
  }
  return out;
}

/** One timeline: calls, log lines, the security plane's records, changes (AGW-41). */
export async function getGatewayActivity(
  gatewayId: string,
  query: GatewayActivityQuery,
): Promise<GatewayActivity> {
  const { data } = await http.get<GatewayActivity>(`/agent-gateways/${gatewayId}/activity`, {
    params: compact(query),
  });
  return data;
}

export async function getGatewayLogs(
  gatewayId: string,
  query: GatewayLogsQuery,
): Promise<GatewayLogsResponse> {
  const { data } = await http.get<GatewayLogsResponse>(`/agent-gateways/${gatewayId}/logs`, {
    params: compact(query),
  });
  return data;
}

/** The same rows as a CSV file (the browser saves it). */
export async function downloadGatewayLogsCsv(
  gatewayId: string,
  query: GatewayLogsQuery,
): Promise<string> {
  const { data } = await http.get<string>(`/agent-gateways/${gatewayId}/logs`, {
    params: { ...compact(query), format: "csv" },
    responseType: "text",
  });
  return data;
}

// --- traces (AGW-13) ---

/** One row per call, or every step of one trace when `trace_id` is set. */
export async function getGatewayTraces(
  gatewayId: string,
  query: GatewayTracesQuery,
): Promise<GatewayTracesResponse> {
  const { data } = await http.get<GatewayTracesResponse>(`/agent-gateways/${gatewayId}/traces`, {
    params: compact(query),
  });
  return data;
}

// --- the Security tab and Isolate (AGW-28) ---

/** The security plane's records for this gateway's workloads, joined with the product's own. */
export async function getGatewaySecurity(
  gatewayId: string,
  query: GatewaySecurityQuery,
): Promise<GatewaySecurity> {
  const { data } = await http.get<GatewaySecurity>(`/agent-gateways/${gatewayId}/security`, {
    params: compact(query),
  });
  return data;
}

/** Isolate an agent: it is stopped and the security plane permits it nothing until released. */
export async function isolateAgent(
  gatewayId: string,
  agentId: string,
  reason?: string,
): Promise<Agent> {
  const { data } = await http.post<Agent>(
    `/agent-gateways/${gatewayId}/agents/${agentId}/isolate`,
    reason ? { reason } : {},
  );
  return data;
}

/** Release an isolated agent: its class comes back; it stays stopped until started. */
export async function releaseAgent(gatewayId: string, agentId: string): Promise<Agent> {
  const { data } = await http.delete<Agent>(
    `/agent-gateways/${gatewayId}/agents/${agentId}/isolate`,
  );
  return data;
}

/** The same rows as a CSV file (the browser saves it). */
export async function downloadGatewayTracesCsv(
  gatewayId: string,
  query: GatewayTracesQuery,
): Promise<string> {
  const { data } = await http.get<string>(`/agent-gateways/${gatewayId}/traces`, {
    params: { ...compact(query), format: "csv" },
    responseType: "text",
  });
  return data;
}

// --- agent endpoints and published agents (AGW-15) ---

export async function listAgentEndpoints(gatewayId: string): Promise<AgentEndpoint[]> {
  const { data } = await http.get<AgentEndpoint[]>(`/agent-gateways/${gatewayId}/agent-endpoints`);
  return data;
}

export async function createAgentEndpoint(
  gatewayId: string,
  payload: AgentEndpointCreateRequest,
): Promise<AgentEndpoint> {
  const { data } = await http.post<AgentEndpoint>(
    `/agent-gateways/${gatewayId}/agent-endpoints`,
    payload,
  );
  return data;
}

export async function getAgentEndpoint(
  gatewayId: string,
  endpointId: string,
): Promise<AgentEndpoint> {
  const { data } = await http.get<AgentEndpoint>(
    `/agent-gateways/${gatewayId}/agent-endpoints/${endpointId}`,
  );
  return data;
}

export async function updateAgentEndpoint(
  gatewayId: string,
  endpointId: string,
  payload: AgentEndpointUpdateRequest,
): Promise<AgentEndpoint> {
  const { data } = await http.put<AgentEndpoint>(
    `/agent-gateways/${gatewayId}/agent-endpoints/${endpointId}`,
    payload,
  );
  return data;
}

export async function deleteAgentEndpoint(gatewayId: string, endpointId: string): Promise<void> {
  await http.delete(`/agent-gateways/${gatewayId}/agent-endpoints/${endpointId}`);
}

/** Fetches the endpoint's agent card and keeps its name, description and skills. */
export async function discoverAgentEndpoint(
  gatewayId: string,
  endpointId: string,
): Promise<AgentEndpoint> {
  const { data } = await http.post<AgentEndpoint>(
    `/agent-gateways/${gatewayId}/agent-endpoints/${endpointId}/discover`,
  );
  return data;
}

export async function listPublishedAgents(gatewayId: string): Promise<PublishedAgent[]> {
  const { data } = await http.get<PublishedAgent[]>(
    `/agent-gateways/${gatewayId}/published-agents`,
  );
  return data;
}

export async function publishAgent(
  gatewayId: string,
  agentId: string,
  payload: PublishRequest,
): Promise<PublishedAgent> {
  const { data } = await http.put<PublishedAgent>(
    `/agent-gateways/${gatewayId}/agents/${agentId}/publish`,
    payload,
  );
  return data;
}

// ---- Content rules (AGW-16) --------------------------------------------------------------
export async function listContentRuleSets(): Promise<ContentRuleSet[]> {
  const { data } = await http.get<ContentRuleSet[]>("/agent-gateways/content-rule-sets");
  return data;
}

export async function listContentRules(gatewayId: string): Promise<ContentRule[]> {
  const { data } = await http.get<ContentRule[]>(`/agent-gateways/${gatewayId}/content-rules`);
  return data;
}

export async function createContentRule(
  gatewayId: string,
  payload: ContentRuleCreateRequest,
): Promise<ContentRule> {
  const { data } = await http.post<ContentRule>(
    `/agent-gateways/${gatewayId}/content-rules`,
    payload,
  );
  return data;
}

export async function updateContentRule(
  gatewayId: string,
  ruleId: string,
  payload: ContentRuleUpdateRequest,
): Promise<ContentRule> {
  const { data } = await http.put<ContentRule>(
    `/agent-gateways/${gatewayId}/content-rules/${ruleId}`,
    payload,
  );
  return data;
}

export async function deleteContentRule(gatewayId: string, ruleId: string): Promise<void> {
  await http.delete(`/agent-gateways/${gatewayId}/content-rules/${ruleId}`);
}

// ---- Off-cloud gateways (AGW-19, D24) -------------------------------------------------
/** A fresh single-use enrolment token and the install line, while the gateway has not enrolled. */
export async function reissueInstallToken(gatewayId: string): Promise<GatewayInstall> {
  const { data } = await http.post<GatewayInstall>(`/agent-gateways/${gatewayId}/install-token`);
  return data;
}

// --- playground (Phase 5 AGW-37) ---

export async function getPlaygroundTargets(gatewayId: string): Promise<PlaygroundTargets> {
  const { data } = await http.get<PlaygroundTargets>(`/agent-gateways/${gatewayId}/playground`);
  return data;
}

export async function runPlayground(
  gatewayId: string,
  payload: PlaygroundRunRequest,
): Promise<PlaygroundRunResult> {
  const { data } = await http.post<PlaygroundRunResult>(
    `/agent-gateways/${gatewayId}/playground/run`,
    payload,
  );
  return data;
}

// --- OpenAPI to tools (Phase 5 AGW-40) ---

export async function previewOpenApi(
  gatewayId: string,
  payload: OpenApiPreviewRequest,
): Promise<OpenApiPreview> {
  const { data } = await http.post<OpenApiPreview>(
    `/agent-gateways/${gatewayId}/tool-servers/openapi-preview`,
    payload,
  );
  return data;
}

export async function refreshOpenApiSpec(gatewayId: string, serverId: string): Promise<ToolServer> {
  const { data } = await http.post<ToolServer>(
    `/agent-gateways/${gatewayId}/tool-servers/${serverId}/refresh-spec`,
  );
  return data;
}

// --- telemetry export (Phase 5 AGW-42) ---

export async function getTelemetryExport(gatewayId: string): Promise<TelemetryExport> {
  const { data } = await http.get<TelemetryExport>(`/agent-gateways/${gatewayId}/telemetry`);
  return data;
}

/** F3: the upstreams this gateway's firewall does not let it reach as saved. */
export async function getUpstreamReach(gatewayId: string): Promise<UpstreamReach> {
  const { data } = await http.get<UpstreamReach>(`/agent-gateways/${gatewayId}/upstream-reach`);
  return data;
}

export async function setTelemetryExport(
  gatewayId: string,
  payload: TelemetryExportRequest,
): Promise<TelemetryExport> {
  const { data } = await http.put<TelemetryExport>(
    `/agent-gateways/${gatewayId}/telemetry`,
    payload,
  );
  return data;
}

export async function removeTelemetryExport(gatewayId: string): Promise<TelemetryExport> {
  const { data } = await http.delete<TelemetryExport>(`/agent-gateways/${gatewayId}/telemetry`);
  return data;
}

// --- budgets (Phase 5 AGW-43) ---

export async function listBudgets(gatewayId: string): Promise<GatewayBudgets> {
  const { data } = await http.get<GatewayBudgets>(`/agent-gateways/${gatewayId}/budgets`);
  return data;
}

export async function putBudget(gatewayId: string, payload: BudgetPutRequest): Promise<Budget> {
  const { data } = await http.put<Budget>(`/agent-gateways/${gatewayId}/budgets`, payload);
  return data;
}

export async function deleteBudget(gatewayId: string, budgetId: string): Promise<void> {
  await http.delete(`/agent-gateways/${gatewayId}/budgets/${budgetId}`);
}

// --- the running configuration (Phase 5 AGW-39) ---

export async function getGatewayConfig(gatewayId: string): Promise<GatewayConfig> {
  const { data } = await http.get<GatewayConfig>(`/agent-gateways/${gatewayId}/config`);
  return data;
}

// --- MCP OAuth 2.1 (Phase 5 AGW-47) ---

export async function getGatewayOAuth(gatewayId: string): Promise<GatewayOAuth> {
  const { data } = await http.get<GatewayOAuth>(`/agent-gateways/${gatewayId}/oauth`);
  return data;
}

export async function setGatewayOAuth(gatewayId: string, enabled: boolean): Promise<GatewayOAuth> {
  const { data } = await http.put<GatewayOAuth>(`/agent-gateways/${gatewayId}/oauth`, { enabled });
  return data;
}

export async function revokeOAuthGrant(gatewayId: string, grantId: string): Promise<void> {
  await http.delete(`/agent-gateways/${gatewayId}/oauth/grants/${grantId}`);
}

export async function removeOAuthClient(gatewayId: string, clientId: string): Promise<void> {
  await http.delete(`/agent-gateways/${gatewayId}/oauth/clients/${clientId}`);
}

export async function getOAuthConsentRequest(requestId: string): Promise<OAuthConsentRequest> {
  const { data } = await http.get<OAuthConsentRequest>(
    `/agent-gateways/oauth-requests/${requestId}`,
  );
  return data;
}

export async function answerOAuthConsent(
  requestId: string,
  answer: { approve: boolean; role?: string | null },
): Promise<{ redirect_to: string }> {
  const { data } = await http.post<{ redirect_to: string }>(
    `/agent-gateways/oauth-requests/${requestId}`,
    answer,
  );
  return data;
}

// --- the gateway's own view, read-only (Phase 5 AGW-45) ---

export async function getAdvancedView(gatewayId: string): Promise<AdvancedView> {
  const { data } = await http.get<AdvancedView>(`/agent-gateways/${gatewayId}/advanced`);
  return data;
}

export async function openAdvancedView(gatewayId: string): Promise<AdvancedViewSession> {
  const { data } = await http.post<AdvancedViewSession>(
    `/agent-gateways/${gatewayId}/advanced/session`,
  );
  return data;
}

/** The gateway's traffic or setup as a Sankey (AGW-49). */
export async function getGatewayFlow(
  gatewayId: string,
  params: { view: FlowView; hours?: number; ownChecks?: boolean },
): Promise<GatewayFlow> {
  const { data } = await http.get<GatewayFlow>(`/agent-gateways/${gatewayId}/flow`, {
    params: { view: params.view, hours: params.hours ?? 24, own_checks: params.ownChecks ?? false },
  });
  return data;
}

